import { useState } from "react";
import { Plus } from "lucide-react";
import type { RatingDraft } from "../../types";
import { Button } from "../Button";
import { CheckboxInput, NumberInput, TextInput } from "../fields";

interface NewRatingBarProps {
  conditionName: string;
  onAdd: (draft: RatingDraft) => Promise<boolean>;
  children?: never;
}

/** One-line form under the ratings table: type a rating and press Add. Clears itself after a successful add. */
export function NewRatingBar({ conditionName, onAdd }: NewRatingBarProps) {
  const [ratingText, setRatingText] = useState("");
  const [time, setTime] = useState("");
  const [debits, setDebits] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [isMedication, setIsMedication] = useState(false);
  const [errors, setErrors] = useState<{ ratingText?: string; debits?: string }>({});
  const [isSaving, setIsSaving] = useState(false);

  const handleAdd = async () => {
    const nextErrors: typeof errors = {};
    if (ratingText.trim() === "") nextErrors.ratingText = "Rating text is required";
    if (debits === null || !Number.isInteger(debits) || debits < 0) nextErrors.debits = "Whole number";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || debits === null) return;
    setIsSaving(true);
    const saved = await onAdd({ ratingText: ratingText.trim(), time: time.trim() || null, debits, notes: notes.trim() || null, isMedication });
    setIsSaving(false);
    if (saved) {
      setRatingText("");
      setTime("");
      setDebits(null);
      setNotes("");
      setIsMedication(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/60 p-3" role="group" aria-label={`Add a rating to ${conditionName}`}>
      <div className="flex flex-wrap items-start gap-3">
        <span className="pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Add a rating</span>
        <div className="min-w-[16rem] flex-1">
          <TextInput id="new-rating-text" label={`New rating for ${conditionName}`} hideLabel value={ratingText} onChange={setRatingText} error={errors.ratingText} placeholder={isMedication ? "Drug name (Rx : is added)" : "Rating text, e.g. Moderate, persistent"} size="sm" />
        </div>
        <div className="pt-1.5">
          <CheckboxInput id="new-rating-is-medication" label="Rx" checked={isMedication} onChange={setIsMedication} />
        </div>
        <div className="w-32">
          <TextInput id="new-rating-time" label="Time for the new rating" hideLabel value={time} onChange={setTime} placeholder="Timing" size="sm" />
        </div>
        <div className="w-24">
          <NumberInput id="new-rating-debits" label="Debits for the new rating" hideLabel value={debits} onChange={setDebits} error={errors.debits} min={0} placeholder="Debits" size="sm" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <TextInput id="new-rating-notes" label="Notes for the new rating" hideLabel value={notes} onChange={setNotes} placeholder="Notes" size="sm" />
        </div>
        <Button size="sm" icon={Plus} onClick={() => void handleAdd()} isLoading={isSaving}>
          Add rating
        </Button>
      </div>
    </div>
  );
}
